import React from 'react';
import {
  PageHead, Section, Eyebrow, PageTitle, Lede, SectionHead, Card, CardTitle, CardBody, Tag, Saying, Button,
} from '../components/ui';

/** Frameworks. Copy carried from the first site's Frameworks tab, on the Diagnose, Drill, Operate spine. */

const STAGES = [
  {
    stage: 'Diagnose',
    line: 'See where you stand.',
    items: [
      {
        name: 'Rumsfeld Radar',
        kind: 'Framework',
        body: 'Maps a value stream by what is known and unknown to show where knowledge is solid and where it is missing. The starting point for any work that will involve AI.',
      },
      {
        name: 'AI Spectrum',
        kind: 'Framework',
        body: 'The taxonomy of modes for human-AI work: five modes from AI Automated through AI Amplified, set by how much human judgment the work requires. The vocabulary practitioners use to say how AI takes part in any piece of work.',
      },
    ],
  },
  {
    stage: 'Drill',
    line: 'Build the capabilities under pressure.',
    items: [
      {
        name: 'Value Flow Framework',
        kind: 'Framework',
        body: 'Maps the eight wastes of Lean thinking to AI capability modes, applied to a specific value stream, in three moves: Map the stream node by node; Eliminate waste before any AI is applied; Augment each remaining node with an AI Spectrum mode. AI should not speed up waste. It should help you see it, remove it, and improve the flow of value.',
        parts: [
          ['Map', 'Lay out the value stream node by node.'],
          ['Eliminate', 'Interrogate value and waste through the eight wastes before any AI is applied.'],
          ['Augment', 'Assign an AI Spectrum mode to each remaining node, by the human judgment it requires.'],
        ],
      },
    ],
  },
  {
    stage: 'Operate',
    line: 'Run the work. Govern the organization.',
    items: [
      {
        name: 'The Steering Cascade',
        kind: 'Framework',
        body: 'The governance structure for AI work, named Steering because the work is directional, and steering is what a discipline does. AI Artisans chair Steering Cascade reviews inside the organization.',
        parts: [
          ['Grounding', 'Excavate the organization’s real values and translate them into testable principles: symbiotic, sustainable, secure, safe.'],
          ['Governance', 'Decision rights and the cross-functional accountability structure, seated during design, before deployment.'],
          ['Guidelines', 'Principles translated into decision criteria: solution guidelines, human agency guidelines, workforce guidelines.'],
          ['Guardrails', 'Limits. What production controls must accomplish. The AI Artisan owns intent; the technical team owns implementation.'],
        ],
      },
      {
        name: 'The Practitioner Commons',
        kind: 'Structure',
        body: 'The structure for social learning inside one organization: cross-functional practitioners connected to each other, exchanging tacit knowledge as they accumulate it. Owned and run by the organization itself, with no facilitator and no vendor in the loop. What one practitioner learns becomes available to all of them fast enough to matter. It runs at 20 percent of the regimen, in a 70-20-10 proportion with game reps and film study.',
        parts: [
          ['Roles', 'Anyone doing the work with the Discipline is a practitioner. AI Artisan is the earned designation: the Apprenticeship, sustained participation, and peer recognition.'],
          ['The result', 'We leave. The capability stays. The Commons is the structural mechanism that makes that possible.'],
        ],
      },
    ],
  },
];

const FrameworksPage = () => (
  <>
    <PageHead
      title="Frameworks"
      description="The five frameworks Right Brain Labs practitioners think with: Rumsfeld Radar, AI Spectrum, Value Flow Framework, the Steering Cascade, and the Practitioner Commons, on the Diagnose, Drill, Operate spine."
      path="/frameworks"
    />
    <Section tone="page" reveal={false}>
      <Eyebrow>Frameworks</Eyebrow>
      <PageTitle>The tools practitioners think with.</PageTitle>
      <Lede>Five frameworks on the Diagnose, Drill, Operate spine. A practitioner diagnoses before they drill, drills before they operate, and keeps doing both while operating, because the Mastery Loop never stops.</Lede>
    </Section>

    {STAGES.map((s, i) => (
      <Section key={s.stage} tone={i % 2 ? 'page' : 'card'} id={s.stage.toLowerCase()}>
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
          <div className="md:col-span-3">
            <div className="font-mono text-meta text-navy-500 dark:text-navy-400">0{i + 1}</div>
            <SectionHead className="mt-1">{s.stage}</SectionHead>
            <p className="font-display text-[19px] mt-2 text-emerald-600 dark:text-emerald-300">{s.line}</p>
          </div>
          <div className="md:col-span-9 flex flex-col gap-[14px]">
            {s.items.map((f) => (
              <Card key={f.name} accent as="article">
                <div className="flex flex-wrap items-center gap-2 mb-2"><Tag>{s.stage}</Tag><Tag>{f.kind}</Tag></div>
                <CardTitle className="text-[20px]">{f.name}</CardTitle>
                <CardBody>{f.body}</CardBody>
                {f.parts && (
                  <dl className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {f.parts.map(([t, d]) => (
                      <div key={t} className="rounded-md bg-navy-50 dark:bg-navy-900 p-4">
                        <dt className="font-sans text-label uppercase font-semibold text-emerald-600 dark:text-emerald-300">{t}</dt>
                        <dd className="font-sans text-interface mt-1 text-navy-600 dark:text-[#C9D2E0]">{d}</dd>
                      </div>
                    ))}
                  </dl>
                )}
              </Card>
            ))}
          </div>
        </div>
      </Section>
    ))}

    <Section tone="page">
      <Saying tag="Creative Exploration" className="mb-12">The machine knows what was. The human imagines what could be.</Saying>
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <Eyebrow>Where they get used</Eyebrow>
          <SectionHead>The frameworks are applied in sequence during the Apprenticeship.</SectionHead>
        </div>
        <Button to="/what-we-do#apprenticeship" variant="secondary">The Apprenticeship</Button>
      </div>
    </Section>
  </>
);

export default FrameworksPage;
